import {
  type Abundance,
  allWholeNumbers,
  totalOf,
} from "@/lib/tool-kit/ecology/abundance";

/**
 * Simpson's diversity measures from species counts.
 *
 * Textbooks call different things "Simpson's index", so this keeps the three apart:
 *
 * - D, dominance: the chance that two individuals drawn at random are the same species.
 * - 1 − D, the Gini–Simpson index: the chance that they are different species.
 * - 1 ÷ D, the inverse Simpson index (Hill number N₂): the "effective number of species".
 *
 * and two ways to compute D from counts nᵢ with total N:
 *
 * - Finite sample, drawing without replacement: D = Σ nᵢ(nᵢ − 1) ÷ (N(N − 1)). This is
 *   Simpson's (1949) original form and an unbiased estimate of the population's Σ pᵢ².
 * - Proportions, with replacement: D = Σ pᵢ², pᵢ = nᵢ ÷ N. It also accepts non-integer
 *   abundances such as percent cover.
 */

export interface SimpsonForm {
  /** D. */
  dominance: number;
  /** 1 − D. */
  diversity: number;
  /** 1 ÷ D, or null when D is zero (every species has a single individual). */
  inverse: number | null;
  /** (1 ÷ D) ÷ S, between 1 ÷ S and 1; null with the inverse. */
  evenness: number | null;
}

export interface SimpsonResult {
  /** S: species with at least one individual. */
  richness: number;
  /** N. */
  total: number;
  /** Σ nᵢ(nᵢ − 1), or null when any count is not a whole number. */
  sumPairs: number | null;
  /** Σ pᵢ². */
  sumSquares: number;
  proportion: SimpsonForm;
  /** Null unless every count is a whole number and N ≥ 2. */
  finite: SimpsonForm | null;
  /** The largest 1 − D the richness allows: 1 − 1 ÷ S. */
  maxDiversity: number;
}

function formFrom(dominance: number, richness: number): SimpsonForm {
  const inverse = dominance > 0 ? 1 / dominance : null;
  return {
    dominance,
    diversity: 1 - dominance,
    inverse,
    evenness: inverse === null ? null : inverse / richness,
  };
}

export function simpson(entries: readonly Abundance[]): SimpsonResult | null {
  const total = totalOf(entries);
  if (entries.length === 0 || !(total > 0)) {
    return null;
  }
  const richness = entries.length;
  let sumSquares = 0;
  for (const entry of entries) {
    sumSquares += (entry.count / total) ** 2;
  }
  let sumPairs: number | null = null;
  let finite: SimpsonForm | null = null;
  if (allWholeNumbers(entries) && total >= 2) {
    let pairs = 0;
    for (const entry of entries) {
      pairs += entry.count * (entry.count - 1);
    }
    sumPairs = pairs;
    finite = formFrom(pairs / (total * (total - 1)), richness);
  }
  return {
    richness,
    total,
    sumPairs,
    sumSquares,
    proportion: formFrom(sumSquares, richness),
    finite,
    maxDiversity: 1 - 1 / richness,
  };
}
